import { useState, useEffect } from 'react'
import { DollarSign, FileDown, RefreshCw, Truck } from 'lucide-react'
import { downloadPdf } from '../../utils/pdfUtils'
import { fretesService } from '../../services/fretesService'
import leiloesService from '../../services/leiloesService'
import { useOrganizador } from '../../contexts/OrganizadorContext'

const fmtBRL = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtData = s => {
  if (!s) return '—'
  try {
    const d = new Date(s)
    return d.toLocaleDateString('pt-BR')
  } catch { return s }
}

export default function Fretes() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [modo, setModo] = useState('leilao') // 'leilao' | 'periodo'
  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [fretes, setFretes] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [buscado, setBuscado] = useState(false)

  useEffect(() => {
    if (!orgId) return
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .catch(() => {})
  }, [orgId])

  const buscar = async () => {
    if (!orgId) return
    setLoading(true)
    setBuscado(false)
    try {
      const filtros = {}
      if (modo === 'leilao' && leilaoId) filtros.leilao_id = leilaoId
      if (modo === 'periodo') {
        if (dataInicio) filtros.data_inicio = dataInicio
        if (dataFim) filtros.data_fim = dataFim
      }
      const res = await fretesService.listar(orgId, filtros)
      setFretes(res.data || [])
      setBuscado(true)
    } catch {
      setFretes([])
    } finally {
      setLoading(false)
    }
  }

  const exportarPdf = async () => {
    setLoadingPdf(true)
    try {
      const filtros = {}
      if (modo === 'leilao' && leilaoId) filtros.leilao_id = leilaoId
      if (modo === 'periodo') {
        if (dataInicio) filtros.data_inicio = dataInicio
        if (dataFim) filtros.data_fim = dataFim
      }
      const res = await fretesService.getPdf(orgId, filtros)
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      await downloadPdf(url, 'relatorio_fretes.pdf')
      URL.revokeObjectURL(url)
    } catch {
      alert('Erro ao gerar PDF')
    } finally {
      setLoadingPdf(false)
    }
  }

  const totalFrete = fretes.reduce((s, f) => s + (parseFloat(f.valor_frete) || 0), 0)

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-yellow-600" />
        <h1 className="text-lg font-semibold text-gray-800">Relatório de Fretes</h1>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          {/* Toggle modo */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden self-end">
            <button
              onClick={() => setModo('leilao')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${modo === 'leilao' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              Por Leilão
            </button>
            <button
              onClick={() => setModo('periodo')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${modo === 'periodo' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              Por Período
            </button>
          </div>

          {modo === 'leilao' ? (
            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
              <label className="text-xs text-gray-500 font-medium">Leilão</label>
              <select
                value={leilaoId}
                onChange={e => setLeilaoId(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
              </select>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 font-medium">Data início</label>
                <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 font-medium">Data fim</label>
                <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </>
          )}

          <button
            onClick={buscar}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white rounded-lg text-sm font-medium transition-colors self-end"
          >
            {loading ? <RefreshCw size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            Buscar
          </button>

          {buscado && fretes.length > 0 && (
            <button
              onClick={exportarPdf}
              disabled={loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-200 text-white rounded-lg text-sm font-medium transition-colors self-end"
            >
              <FileDown size={15} />
              {loadingPdf ? 'Gerando...' : 'Exportar PDF'}
            </button>
          )}
        </div>
      </div>

      {/* Resultados */}
      {buscado && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700">
              {fretes.length} {fretes.length === 1 ? 'frete encontrado' : 'fretes encontrados'}
            </h3>
            {fretes.length > 0 && (
              <span className="text-sm font-semibold text-gray-900">Total: {fmtBRL(totalFrete)}</span>
            )}
          </div>

          {fretes.length === 0 ? (
            <div className="p-12 text-center text-gray-400 text-sm">
              Nenhum frete registrado para o filtro selecionado.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motorista</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Placa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comprador</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leilão</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Animais</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Frete</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {fretes.map(f => {
                    const lotes = f.descricao_json || []
                    const totalAnim = Array.isArray(lotes) ? lotes.reduce((s, l) => s + (l.qtd || 0), 0) : 0
                    const descricao = Array.isArray(lotes) && lotes.length > 0
                      ? lotes.map(l => `L${l.numero_lote}${l.descricao ? ` ${l.descricao}` : ''}`).join(', ')
                      : null
                    return (
                      <tr key={f.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{fmtData(f.data_frete)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{f.motorista_nome || '—'}</td>
                        <td className="px-4 py-3 text-sm font-mono text-gray-700">{f.placa || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{f.comprador_nome || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-[180px] truncate">{f.leilao_nome || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {totalAnim > 0 && <span className="mr-1 font-medium">{totalAnim} animais</span>}
                          {descricao && <span className="text-xs text-gray-400 block truncate max-w-[200px]" title={descricao}>{descricao}</span>}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">{fmtBRL(f.valor_frete)}</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 border-t-2 border-gray-200">
                    <td colSpan={6} className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">Total</td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">{fmtBRL(totalFrete)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
